import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig, isClickedOutside, onAnimationEnd } from '@theme/utilities';

// Delay before opening so the auto-opening cart drawer enters the top layer
// first and this modal stacks above it.
const OPEN_DELAY = 250;

// Debounce for cart re-fetches when cart:update events fire in bursts.
const SYNC_DEBOUNCE = 150;

// Dispatch this on `document` to open the modal from elsewhere (e.g. the
// frame upsell hero block on the cart page).
export const OPEN_EVENT = 'add-to-cart-upsell:open';

/**
 * Add-to-cart upsell modal.
 *
 * Product context: keeps a snapshot of the cart and, whenever the theme's
 * cart:update event fires, diffs quantities for this product's variants.
 * A genuine increase of an unframed variant opens the "Get it framed" panel;
 * an increase of a framed variant opens the "Complete the set" panel (when
 * the product belongs to an inspirational collection).
 *
 * Cart context: never auto-opens. Listens for the OPEN_EVENT document event
 * and opens the frame panel, listing every unframed line in the cart, each
 * with its own frame style tiles.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLScriptElement} data - JSON configuration payload.
 * @property {HTMLElement} [frameItems] - Container of frame item rows.
 * @property {HTMLTemplateElement} [frameRowTemplate] - Row template (cart context).
 * @property {HTMLTemplateElement} [frameTileTemplate] - Tile template (cart context).
 * @property {HTMLElement} [frameDismiss] - Frame panel dismiss button.
 * @property {HTMLButtonElement} [frameCta] - Frame panel CTA.
 * @property {HTMLElement} [frameCtaLabel] - Frame panel CTA label.
 * @property {HTMLElement} [frameError] - Error element (frame panel).
 * @property {HTMLElement} [setProducts] - Container of set product tiles.
 * @property {HTMLElement} [setCount] - "N prints selected" element.
 * @property {HTMLElement} [setTotal] - Set total price element.
 * @property {HTMLButtonElement} [setCta] - Set panel CTA.
 * @property {HTMLElement} [setCtaLabel] - Set panel CTA label.
 * @property {HTMLElement} [setError] - Error element (set panel).
 *
 * @extends {Component<Refs>}
 */
class AddToCartUpsell extends Component {
  requiredRefs = ['dialog', 'data'];

  #abortController = new AbortController();

  /** @type {any} */
  #config = null;

  /** @type {Map<string, number> | null} Quantity per variant id of this product */
  #baseline = null;

  /** @type {number | undefined} */
  #syncTimer;

  /** @type {'frame' | 'set' | null} */
  #currentPanel = null;

  #busy = false;

  /** Whether the frame panel was opened by intercepting a checkout attempt. */
  #checkoutIntent = false;

  /** Set once we hand over to checkout, so we never intercept again. */
  #proceeding = false;

  /** @type {Map<string, any>} Cache of /products/{handle}.js payloads */
  #productCache = new Map();

  /** @type {number | undefined} */
  #linksTimer;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#config = JSON.parse(this.refs.data.textContent || 'null');
    } catch (error) {
      console.error('[add-to-cart-upsell] Invalid configuration JSON', error);
      return;
    }

    if (!this.#config) return;

    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartActivity, { signal });
    document.addEventListener(OPEN_EVENT, this.#onOpenRequest, { signal });

    // Keep the closing animation when Esc is pressed.
    this.refs.dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.dismiss();
      },
      { signal }
    );

    // Close when clicking the backdrop.
    this.refs.dialog.addEventListener(
      'click',
      (event) => {
        if (isClickedOutside(event, this.refs.dialog)) this.dismiss();
      },
      { signal }
    );

    // Establish the cart baseline for diffing.
    if (this.#config.context === 'product') {
      this.#syncCart();
    }

    if (this.#config.context === 'cart') {
      // Intercept checkout attempts while unframed prints are in the cart.
      document.addEventListener('click', this.#onCheckoutClick, { capture: true, signal });
      document.addEventListener('submit', this.#onCheckoutSubmit, { capture: true, signal });

      // "Add a frame" links under unframed lines on the cart page.
      this.#scheduleCartRowLinks();
      document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleCartRowLinks, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#syncTimer) clearTimeout(this.#syncTimer);
    if (this.#linksTimer) clearTimeout(this.#linksTimer);
  }

  /* ------------------------------------------------------------------ */
  /* Triggering                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Debounced reaction to any cart:update event.
   */
  #onCartActivity = () => {
    if (this.#config.context !== 'product') return;
    if (this.#syncTimer) clearTimeout(this.#syncTimer);
    this.#syncTimer = setTimeout(() => this.#syncCart(), SYNC_DEBOUNCE);
  };

  /**
   * Fetches the cart, diffs this product's variant quantities against the
   * previous snapshot and triggers the appropriate panel.
   */
  async #syncCart() {
    let cart;
    try {
      cart = await this.#fetchCart();
    } catch (error) {
      console.error('[add-to-cart-upsell] Unable to fetch cart', error);
      return;
    }

    const previous = this.#baseline;
    this.#baseline = this.#quantitiesFor(cart);

    // First fetch only establishes the baseline.
    if (!previous) return;
    if (this.refs.dialog.open || this.#busy) return;

    let noFrameIncrease = null;
    let framedIncrease = null;

    for (const variant of this.#config.variants) {
      const id = String(variant.id);
      const delta = (this.#baseline.get(id) ?? 0) - (previous.get(id) ?? 0);
      if (delta <= 0) continue;

      const frameValue = variant.options[this.#config.frameOptionIndex];
      if (this.#isNoFrame(frameValue, this.#config.noFrameValue)) {
        noFrameIncrease = { variant, delta };
      } else {
        framedIncrease = { variant, delta };
      }
    }

    if (noFrameIncrease) {
      this.#prepareProductFramePanel(noFrameIncrease.variant, noFrameIncrease.delta);
    } else if (framedIncrease && this.#config.set?.products?.length) {
      const frameValue = framedIncrease.variant.options[this.#config.frameOptionIndex];
      this.#prepareSetPanel(frameValue, cart);
    }
  }

  /**
   * Opens the frame panel on request (hero button / cart row links).
   */
  #onOpenRequest = () => {
    if (!this.#isPrimaryCartInstance()) return;
    this.#openCartFramePanel({ checkoutIntent: false });
  };

  /**
   * Intercepts clicks on checkout buttons while unframed prints are in the
   * cart, showing the frame upsell first. "Continue with no frame" (or a
   * successful upgrade) proceeds to checkout.
   *
   * @param {MouseEvent} event
   */
  #onCheckoutClick = (event) => {
    if (!this.#isPrimaryCartInstance() || this.#proceeding) return;
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[name="checkout"], .cart__checkout-button');
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#openCartFramePanel({ checkoutIntent: true });
  };

  /**
   * @param {SubmitEvent} event
   */
  #onCheckoutSubmit = (event) => {
    if (!this.#isPrimaryCartInstance() || this.#proceeding) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || !form.action.includes('/checkout')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#openCartFramePanel({ checkoutIntent: true });
  };

  /**
   * If the block (and therefore this modal) is rendered more than once, only
   * the first cart-context instance responds to document-level triggers.
   *
   * @returns {boolean}
   */
  #isPrimaryCartInstance() {
    if (this.#config.context !== 'cart') return false;
    return document.querySelector('add-to-cart-upsell[data-context="cart"]') === this;
  }

  /**
   * Builds the frame panel from the live cart — one row per unframed line —
   * and opens it. With checkoutIntent, an empty result proceeds straight to
   * checkout instead.
   *
   * @param {{ checkoutIntent: boolean }} options
   */
  async #openCartFramePanel({ checkoutIntent }) {
    if (this.#busy || this.refs.dialog.open) return;

    const container = this.refs.frameItems;
    const rowTemplate = this.refs.frameRowTemplate;
    const tileTemplate = this.refs.frameTileTemplate;
    if (!container || !(rowTemplate instanceof HTMLTemplateElement) || !(tileTemplate instanceof HTMLTemplateElement)) {
      if (checkoutIntent) this.#proceedToCheckout();
      return;
    }

    let cart;
    try {
      cart = await this.#fetchCart();
    } catch (error) {
      console.error('[add-to-cart-upsell] Unable to fetch cart', error);
      if (checkoutIntent) this.#proceedToCheckout();
      return;
    }

    const unframedLines = cart.items.filter((item) => this.#lineIsUnframed(item));

    if (unframedLines.length === 0) {
      if (checkoutIntent) this.#proceedToCheckout();
      return;
    }

    container.innerHTML = '';

    for (const line of unframedLines) {
      try {
        const productData = await this.#productByHandle(line.handle);
        const rowConfig = this.#rowConfigFromProduct(productData, line);
        if (!rowConfig) continue;

        const unframedVariant = rowConfig.variants.find(
          /** @param {any} v */ (v) => String(v.id) === String(line.variant_id)
        );
        if (!unframedVariant) continue;

        const row = this.#buildFrameRow(rowTemplate, tileTemplate, rowConfig, line);
        if (!this.#populateFrameRow(row, rowConfig, unframedVariant, line.quantity, line.image)) continue;

        row.dataset.lineKey = line.key;
        container.appendChild(row);
      } catch (error) {
        console.error('[add-to-cart-upsell] Skipping cart line', line.handle, error);
      }
    }

    if (container.children.length === 0) {
      if (checkoutIntent) this.#proceedToCheckout();
      return;
    }

    this.#checkoutIntent = checkoutIntent;
    this.#applyDismissLabel();
    this.#updateFrameCta();
    this.#showError(this.refs.frameError, null);
    this.#open('frame');
  }

  /**
   * @param {any} item - A cart line from the AJAX API.
   * @returns {boolean}
   */
  #lineIsUnframed(item) {
    const optionMatch = (item.options_with_values || []).some(
      /** @param {any} option */
      (option) => {
        const value = String(option.value || '').toLowerCase();
        return value.includes('no frame') || value === 'unframed';
      }
    );
    const titleMatch = String(item.variant_title || '').toLowerCase() === 'no frame';
    return optionMatch || titleMatch;
  }

  /**
   * Fetches and caches /products/{handle}.js.
   *
   * @param {string} handle
   * @returns {Promise<any>}
   */
  async #productByHandle(handle) {
    if (this.#productCache.has(handle)) return this.#productCache.get(handle);
    const response = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Product fetch failed: ${handle}`);
    const data = await response.json();
    this.#productCache.set(handle, data);
    return data;
  }

  /**
   * Builds a row config ({ variants, frameOptionIndex, noFrameValue, frameValues })
   * from a /products/{handle}.js payload.
   *
   * @param {any} productData
   * @param {any} line - The cart line, used to identify the unframed value.
   * @returns {any | null}
   */
  #rowConfigFromProduct(productData, line) {
    const frameOptionIndex = (productData.options || []).findIndex(
      /** @param {any} option */
      (option) => String(option.name || option).toLowerCase().includes('frame')
    );
    if (frameOptionIndex < 0) return null;

    const option = productData.options[frameOptionIndex];
    const values = option.values || [];

    let noFrameValue =
      values.find(/** @param {string} v */ (v) => {
        const value = v.toLowerCase();
        return value.includes('no frame') || value === 'unframed';
      }) ?? '';

    if (!noFrameValue) {
      const lineOption = (line.options_with_values || []).find(
        /** @param {any} o */ (o) => {
          const value = String(o.value || '').toLowerCase();
          return value.includes('no frame') || value === 'unframed';
        }
      );
      noFrameValue = lineOption ? lineOption.value : '';
    }

    return {
      frameOptionIndex,
      noFrameValue,
      frameValues: values.filter(/** @param {string} v */ (v) => v !== noFrameValue),
      variants: (productData.variants || []).map(
        /** @param {any} v */
        (v) => ({
          id: v.id,
          available: v.available,
          priceCents: v.price,
          price: this.#formatMoney(v.price) ?? '',
          options: v.options,
          image: v.featured_image?.src ? this.#sizedImage(v.featured_image.src, 300) : null,
        })
      ),
    };
  }

  /**
   * Clones the row + tile templates into a populated (but not yet priced)
   * frame row for a cart line.
   *
   * @param {HTMLTemplateElement} rowTemplate
   * @param {HTMLTemplateElement} tileTemplate
   * @param {any} rowConfig
   * @param {any} line
   * @returns {HTMLElement}
   */
  #buildFrameRow(rowTemplate, tileTemplate, rowConfig, line) {
    const fragment = /** @type {DocumentFragment} */ (rowTemplate.content.cloneNode(true));
    const row = /** @type {HTMLElement} */ (fragment.querySelector('[data-frame-item]'));
    const grid = row.querySelector('.atc-upsell__frame-grid');

    const title = row.querySelector('.atc-upsell__item-title');
    if (title) title.textContent = line.product_title;

    const meta = row.querySelector('.atc-upsell__item-meta');
    if (meta instanceof HTMLElement) {
      meta.hidden = line.quantity <= 1;
      meta.textContent = line.quantity > 1 ? `Qty ${line.quantity}` : '';
    }

    if (grid) {
      grid.setAttribute('aria-label', `Frame style for ${line.product_title}`);

      const makeTile = (/** @type {string} */ frameValue, /** @type {string} */ label) => {
        const tileFragment = /** @type {DocumentFragment} */ (tileTemplate.content.cloneNode(true));
        const tile = /** @type {HTMLElement} */ (tileFragment.querySelector('[role="radio"]'));
        tile.dataset.frameValue = frameValue;
        const tileLabel = tile.querySelector('.atc-upsell__tile-label');
        if (tileLabel) tileLabel.textContent = label;
        return tile;
      };

      for (const value of rowConfig.frameValues) {
        grid.appendChild(makeTile(value, value));
      }

      const keepTile = makeTile('', 'Keep unframed');
      const keepImage = keepTile.querySelector('[data-tile-image]');
      if (keepImage instanceof HTMLImageElement && line.image) {
        keepImage.src = this.#sizedImage(line.image, 300);
        keepImage.hidden = false;
      }
      grid.appendChild(keepTile);
    }

    return row;
  }

  /**
   * @param {string} src
   * @param {number} width
   * @returns {string}
   */
  #sizedImage(src, width) {
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  }

  /**
   * Proceeds to checkout, permanently disarming interception for this page.
   */
  #proceedToCheckout() {
    this.#proceeding = true;
    window.location.href = '/checkout';
  }

  /**
   * Panel dismiss button: continues to checkout when the panel was opened by
   * a checkout attempt, otherwise dismisses normally.
   */
  continueOrDismiss = () => {
    if (this.#checkoutIntent) {
      this.#proceedToCheckout();
      return;
    }
    this.dismiss();
  };

  #applyDismissLabel() {
    const dismiss = this.refs.frameDismiss;
    if (!(dismiss instanceof HTMLElement)) return;
    const label = this.#checkoutIntent ? dismiss.dataset.labelCheckout : dismiss.dataset.labelDefault;
    if (label) dismiss.textContent = label;
  }

  /**
   * Injects "Add a frame" links under unframed lines on the cart page.
   */
  #scheduleCartRowLinks = () => {
    if (!this.#isPrimaryCartInstance()) return;
    if (this.#linksTimer) clearTimeout(this.#linksTimer);
    this.#linksTimer = setTimeout(() => this.#injectCartRowLinks(), 200);
  };

  async #injectCartRowLinks() {
    const rows = document.querySelectorAll('.cart-items__table-row[data-key]');
    if (rows.length === 0) return;

    let cart;
    try {
      cart = await this.#fetchCart();
    } catch (_) {
      return;
    }

    document.querySelectorAll('.atc-upsell-row-link').forEach((el) => el.remove());

    for (const item of cart.items) {
      if (!this.#lineIsUnframed(item)) continue;

      const row = document.querySelector(`.cart-items__table-row[data-key="${item.key}"]`);
      const details = row?.querySelector('.cart-items__details');
      if (!details || details.querySelector('.atc-upsell-row-link')) continue;

      const link = document.createElement('a');
      link.href = '#';
      link.className = 'atc-upsell-row-link';
      link.textContent = 'Add a frame';
      link.style.cssText =
        'display:block;margin-top:8px;text-decoration:underline;text-underline-offset:0.25em;cursor:pointer;font-family:var(--font-accent--family);font-size:0.875rem;color:var(--color-foreground,#000);';
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.#openCartFramePanel({ checkoutIntent: false });
      });

      details.appendChild(link);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Frame panel                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Product context: populates the single item row for the variant that was
   * just added and opens the panel.
   *
   * @param {any} addedVariant - The unframed variant that was added.
   * @param {number} quantity - The quantity that was added.
   */
  #prepareProductFramePanel(addedVariant, quantity) {
    if (this.#isDismissed('frame')) return;

    const container = this.refs.frameItems;
    const row = container?.querySelector('[data-frame-item]');
    if (!(row instanceof HTMLElement)) return;

    const rowConfig = {
      variants: this.#config.variants,
      frameOptionIndex: this.#config.frameOptionIndex,
      noFrameValue: this.#config.noFrameValue,
    };

    if (!this.#populateFrameRow(row, rowConfig, addedVariant, quantity)) return;

    const meta = row.querySelector('[data-item-meta]');
    if (meta instanceof HTMLElement) {
      meta.hidden = quantity <= 1;
      meta.textContent = quantity > 1 ? `Qty ${quantity}` : '';
    }

    row.hidden = false;
    this.#updateFrameCta();
    this.#showError(this.refs.frameError, null);
    this.#open('frame');
  }

  /**
   * Populates a frame item row: resolves the framed counterpart for each
   * chip, prices the upgrade, hides chips with no available match and
   * selects the first framed option.
   *
   * @param {HTMLElement} row
   * @param {any} rowConfig - { variants, frameOptionIndex, noFrameValue }
   * @param {any} unframedVariant
   * @param {number} quantity
   * @param {string | null} [lineImage] - The cart line's image, if any.
   * @returns {boolean} Whether the row has at least one framed option.
   */
  #populateFrameRow(row, rowConfig, unframedVariant, quantity, lineImage = null) {
    row.dataset.unframedVariantId = String(unframedVariant.id);
    row.dataset.unframedPriceCents = String(unframedVariant.priceCents);
    row.dataset.quantity = String(quantity);

    // The print's own imagery, used for the "Keep unframed" tile and as a
    // fallback for framed tiles whose variants have no image of their own.
    const fallbackImage = unframedVariant.image || lineImage || this.#config.productImage || null;

    let firstFramedTile = null;

    for (const tile of row.querySelectorAll('[data-frame-value]')) {
      if (!(tile instanceof HTMLElement)) continue;

      const frameValue = tile.dataset.frameValue ?? '';
      tile.setAttribute('aria-checked', 'false');

      const image = tile.querySelector('[data-tile-image]');

      // "Keep unframed" tile: always available. Its image is usually
      // server-rendered; fill it in from variant data if it's missing.
      if (frameValue === '') {
        tile.hidden = false;
        if (image instanceof HTMLImageElement) {
          if (unframedVariant.image) {
            image.src = unframedVariant.image;
            image.hidden = false;
          } else if (!image.getAttribute('src') && fallbackImage) {
            image.src = fallbackImage;
            image.hidden = false;
          }
        }
        continue;
      }

      const target = this.#findMatchingVariant(rowConfig, unframedVariant.options, frameValue);

      if (!target) {
        tile.hidden = true;
        continue;
      }

      tile.hidden = false;
      tile.dataset.variantId = String(target.id);
      tile.dataset.priceCents = String(target.priceCents);

      if (image instanceof HTMLImageElement) {
        const src = target.image || fallbackImage;
        image.hidden = !src;
        if (src) image.src = src;
      }

      const priceElement = tile.querySelector('[data-price]');
      if (priceElement) {
        const delta = target.priceCents - unframedVariant.priceCents;
        const formattedDelta = delta > 0 ? this.#formatMoney(delta) : null;
        priceElement.textContent = formattedDelta ? `+${formattedDelta}` : '';
      }

      if (!firstFramedTile) firstFramedTile = tile;
    }

    if (!firstFramedTile) return false;

    firstFramedTile.setAttribute('aria-checked', 'true');
    return true;
  }

  /**
   * Handles a click on a frame style tile (radio within its row).
   *
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  selectFrameOption(event) {
    const tile = event.target.closest('[data-frame-value]');
    const row = event.target.closest('[data-frame-item]');
    if (!(tile instanceof HTMLElement) || !(row instanceof HTMLElement) || tile.hidden) return;

    for (const other of row.querySelectorAll('[data-frame-value]')) {
      other.setAttribute('aria-checked', other === tile ? 'true' : 'false');
    }

    this.#updateFrameCta();
  }

  /**
   * @returns {{ row: HTMLElement, framedVariantId: string, framedPriceCents: number, unframedVariantId: string, unframedPriceCents: number, quantity: number, lineKey: string | undefined }[]}
   */
  #frameSelections() {
    const container = this.refs.frameItems;
    if (!container) return [];

    const selections = [];

    for (const row of container.querySelectorAll('[data-frame-item]')) {
      if (!(row instanceof HTMLElement) || row.hidden) continue;

      const tile = row.querySelector('[data-frame-value][aria-checked="true"]');
      if (!(tile instanceof HTMLElement)) continue;
      if ((tile.dataset.frameValue ?? '') === '') continue; // Keep unframed
      if (!tile.dataset.variantId) continue;

      selections.push({
        row,
        framedVariantId: tile.dataset.variantId,
        framedPriceCents: parseInt(tile.dataset.priceCents ?? '0', 10) || 0,
        unframedVariantId: row.dataset.unframedVariantId ?? '',
        unframedPriceCents: parseInt(row.dataset.unframedPriceCents ?? '0', 10) || 0,
        quantity: parseInt(row.dataset.quantity ?? '1', 10) || 1,
        lineKey: row.dataset.lineKey,
      });
    }

    return selections;
  }

  #updateFrameCta() {
    const { frameCta, frameCtaLabel } = this.refs;
    if (!frameCta || !frameCtaLabel) return;

    const selections = this.#frameSelections();
    const count = selections.length;

    frameCta.disabled = count === 0;

    if (count === 0) {
      frameCtaLabel.textContent = 'No frames selected';
      return;
    }

    const base = count === 1 ? frameCta.dataset.labelBase || 'Upgrade my print' : `Upgrade ${count} prints`;
    const totalDelta = selections.reduce(
      (sum, s) => sum + Math.max(s.framedPriceCents - s.unframedPriceCents, 0) * s.quantity,
      0
    );
    const formattedDelta = totalDelta > 0 ? this.#formatMoney(totalDelta) : null;

    frameCtaLabel.textContent = formattedDelta ? `${base} \u00b7 +${formattedDelta}` : base;
  }

  /**
   * Confirms the frame upgrades: adds the framed variants and, in 'swap'
   * mode, decrements each corresponding unframed line.
   */
  async confirmFrame() {
    if (this.#busy) return;

    const selections = this.#frameSelections();
    if (selections.length === 0) return;

    this.#setBusy(true);
    this.#showError(this.refs.frameError, null);

    let cart;

    try {
      // 1. Add all framed variants first so the prints never disappear from
      //    the cart mid-operation.
      const items = selections.map((s) => ({ id: Number(s.framedVariantId), quantity: s.quantity }));
      const addResponse = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items }) })
      );
      const addResult = await addResponse.json();
      if (addResult.status) {
        throw new Error(addResult.description || addResult.message || 'Unable to add to cart');
      }

      cart = await this.#fetchCart();

      // 2. In swap mode, decrement each unframed line by the quantity upgraded.
      if (this.#config.behavior === 'swap') {
        for (const selection of selections) {
          const line =
            (selection.lineKey &&
              cart.items.find(/** @param {any} item */ (item) => item.key === selection.lineKey)) ||
            cart.items.find(
              /** @param {any} item */
              (item) => String(item.variant_id) === String(selection.unframedVariantId)
            );

          if (!line) continue;

          const newQuantity = Math.max(line.quantity - selection.quantity, 0);
          const changeResponse = await fetch(
            `${Theme.routes.cart_change_url}.js`,
            fetchConfig('json', { body: JSON.stringify({ id: line.key, quantity: newQuantity }) })
          );
          try {
            const changed = await changeResponse.json();
            if (changed && Array.isArray(changed.items)) cart = changed;
          } catch (_) {
            // Non-JSON response — re-fetch the cart below.
            cart = await this.#fetchCart();
          }
        }
      }

      this.#baseline = this.#quantitiesFor(cart);
    } catch (error) {
      // Only a genuine cart mutation failure reaches here.
      console.error('[add-to-cart-upsell] Frame upgrade failed', error);
      this.#showError(
        this.refs.frameError,
        'Something went wrong updating your basket. Please try again.'
      );
      this.#setBusy(false);
      return;
    }

    this.#setBusy(false);

    // The basket is updated — everything below is best-effort UI work and
    // must never surface an error to the customer.

    // Opened by a checkout attempt: hand straight over to checkout.
    if (this.#checkoutIntent) {
      this.#proceedToCheckout();
      return;
    }

    this.#markDismissed('frame');
    this.#notifyCartUpdated(cart, selections[0].framedVariantId);
    await this.close();

    // Chain into "Complete the set" now that a framed print is in the cart.
    try {
      if (this.#config.context === 'product' && this.#config.set?.products?.length) {
        const framedVariant = this.#config.variants.find(
          /** @param {any} v */ (v) => String(v.id) === selections[0].framedVariantId
        );
        if (framedVariant) {
          const frameValue = framedVariant.options[this.#config.frameOptionIndex];
          this.#prepareSetPanel(frameValue, cart);
        }
      }
    } catch (error) {
      console.warn('[add-to-cart-upsell] Set panel chain skipped', error);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Set panel                                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Populates and opens the "Complete the set" panel.
   *
   * @param {string} frameValue - The frame style just chosen, used to match
   *   the sibling products' variants.
   * @param {any} [knownCart] - Cart object, if already fetched.
   */
  async #prepareSetPanel(frameValue, knownCart) {
    if (this.#isDismissed('set')) return;

    const container = this.refs.setProducts;
    if (!container) return;

    let cart = knownCart;
    if (!cart) {
      try {
        cart = await this.#fetchCart();
      } catch (error) {
        console.error('[add-to-cart-upsell] Unable to fetch cart', error);
        return;
      }
    }

    const productIdsInCart = new Set(
      cart.items.map(/** @param {any} item */ (item) => String(item.product_id))
    );

    let anyVisible = false;

    for (const tile of container.querySelectorAll('[data-sibling-id]')) {
      if (!(tile instanceof HTMLElement)) continue;

      const siblingId = tile.dataset.siblingId ?? '';
      const product = this.#config.set.products.find(
        /** @param {any} p */ (p) => String(p.id) === siblingId
      );

      // Skip products that are unknown or already in the basket.
      if (!product || productIdsInCart.has(siblingId)) {
        tile.hidden = true;
        continue;
      }

      // Prefer the variant matching the frame style just chosen.
      let variant = null;
      if (product.frameOptionIndex >= 0) {
        variant = this.#findVariantByFrameValue(product, frameValue);
      }
      if (!variant) {
        variant = product.variants.find(/** @param {any} v */ (v) => v.available) ?? null;
      }

      if (!variant) {
        tile.hidden = true;
        continue;
      }

      tile.hidden = false;
      tile.dataset.selected = 'true';
      tile.setAttribute('aria-pressed', 'true');
      tile.dataset.variantId = String(variant.id);
      tile.dataset.priceCents = String(variant.priceCents);

      const priceElement = tile.querySelector('[data-price]');
      if (priceElement) priceElement.textContent = variant.price;

      anyVisible = true;
    }

    if (!anyVisible) return;

    this.#updateSetTotals();
    this.#showError(this.refs.setError, null);
    this.#open('set');
  }

  /**
   * Toggles a set product tile in or out of the selection.
   *
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  toggleSetItem(event) {
    const tile = event.target.closest('[data-sibling-id]');
    if (!(tile instanceof HTMLElement) || tile.hidden) return;

    const selected = tile.dataset.selected !== 'true';
    tile.dataset.selected = selected ? 'true' : 'false';
    tile.setAttribute('aria-pressed', selected ? 'true' : 'false');
    this.#updateSetTotals();
  }

  /**
   * @returns {HTMLElement[]} The currently selected, visible set tiles.
   */
  #selectedSetTiles() {
    const container = this.refs.setProducts;
    if (!container) return [];
    return Array.from(container.querySelectorAll('[data-sibling-id]')).filter(
      (tile) =>
        tile instanceof HTMLElement && !tile.hidden && tile.dataset.selected === 'true'
    );
  }

  #updateSetTotals() {
    const tiles = this.#selectedSetTiles();
    const count = tiles.length;
    const totalCents = tiles.reduce(
      (sum, tile) => sum + (parseInt(tile.dataset.priceCents ?? '0', 10) || 0),
      0
    );

    const { setCount, setTotal, setCta, setCtaLabel } = this.refs;

    if (setCount) {
      setCount.textContent = count === 1 ? '1 print selected' : `${count} prints selected`;
    }
    if (setTotal) {
      setTotal.textContent = count > 0 ? this.#formatMoney(totalCents) ?? '' : '';
    }
    if (setCta) setCta.disabled = count === 0;
    if (setCtaLabel) {
      setCtaLabel.textContent =
        count === 0
          ? 'Select a print'
          : count === 1
            ? 'Add to basket'
            : count === 2
              ? 'Add both to basket'
              : `Add ${count} prints to basket`;
    }
  }

  /**
   * Adds the selected set products to the cart.
   */
  async addSet() {
    if (this.#busy) return;

    const tiles = this.#selectedSetTiles();
    if (tiles.length === 0) return;

    this.#setBusy(true);
    this.#showError(this.refs.setError, null);

    try {
      const items = tiles.map((tile) => ({
        id: Number(tile.dataset.variantId),
        quantity: 1,
      }));

      const addResponse = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items }) })
      );
      const addResult = await addResponse.json();
      if (addResult.status) {
        throw new Error(addResult.description || addResult.message || 'Unable to add to cart');
      }

      const cart = await this.#fetchCart();
      this.#baseline = this.#quantitiesFor(cart);

      this.#setBusy(false);
      this.#markDismissed('set');
      this.#notifyCartUpdated(cart);
      this.close();
    } catch (error) {
      console.error('[add-to-cart-upsell] Complete-the-set failed', error);
      this.#showError(
        this.refs.setError,
        'Something went wrong updating your basket. Please try again.'
      );
      this.#setBusy(false);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Shared helpers                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * @param {string | undefined} value
   * @param {string | undefined} noFrameValue
   * @returns {boolean}
   */
  #isNoFrame(value, noFrameValue) {
    const noFrame = (noFrameValue || '').toLowerCase();
    if (!noFrame || !value) return false;
    return value.toLowerCase() === noFrame;
  }

  /**
   * Finds the variant of `config` that matches `referenceOptions` in every
   * position except the frame option, which must equal `frameValue`.
   *
   * @param {any} config - Object with `variants` and `frameOptionIndex`.
   * @param {string[]} referenceOptions - Option values of the reference variant.
   * @param {string} frameValue - Desired frame style value.
   * @returns {any | null}
   */
  #findMatchingVariant(config, referenceOptions, frameValue) {
    return (
      config.variants.find(
        /** @param {any} v */ (v) =>
          v.available &&
          v.options.every(
            /**
             * @param {string} option
             * @param {number} index
             */
            (option, index) =>
              index === config.frameOptionIndex
                ? option.toLowerCase() === frameValue.toLowerCase()
                : option === referenceOptions[index]
          )
      ) ?? null
    );
  }

  /**
   * Finds an available variant of a sibling product with the given frame value.
   *
   * @param {any} product
   * @param {string} frameValue
   * @returns {any | null}
   */
  #findVariantByFrameValue(product, frameValue) {
    return (
      product.variants.find(
        /** @param {any} v */ (v) =>
          v.available &&
          (v.options[product.frameOptionIndex] ?? '').toLowerCase() === frameValue.toLowerCase()
      ) ?? null
    );
  }

  /**
   * @returns {Promise<any>} The cart object from the AJAX API.
   */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    return response.json();
  }

  /**
   * Maps this product's variant ids to their quantities in the given cart.
   *
   * @param {any} cart
   * @returns {Map<string, number>}
   */
  #quantitiesFor(cart) {
    const quantities = new Map();

    // Cart context has no variants config — the baseline diff only applies
    // on product pages.
    const variants = this.#config.variants;
    if (!Array.isArray(variants)) return quantities;

    const ids = new Set(variants.map(/** @param {any} v */ (v) => String(v.id)));
    for (const item of cart.items) {
      const id = String(item.variant_id);
      if (ids.has(id)) quantities.set(id, (quantities.get(id) ?? 0) + item.quantity);
    }
    return quantities;
  }

  /**
   * Dispatches a cart update so the cart drawer, cart items, cart bubble and
   * any other listeners (e.g. the frame upsell hero) refresh themselves.
   *
   * @param {any} cart - The latest cart object from the AJAX API.
   * @param {string} [variantId]
   */
  #notifyCartUpdated(cart, variantId) {
    const sourceId = this.id || 'add-to-cart-upsell';
    const data = {
      source: 'add-to-cart-upsell',
      itemCount: cart.item_count,
      variantId,
    };

    try {
      this.dispatchEvent(new CartUpdateEvent(cart, sourceId, data));
    } catch (error) {
      // Theme version mismatch on the event class — dispatch the same shape
      // as a plain CustomEvent so cart components still refresh.
      console.warn('[add-to-cart-upsell] CartUpdateEvent unavailable, using CustomEvent', error);
      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId, data },
        })
      );
    }
  }

  /**
   * Shows the requested panel. Opening is slightly delayed so the modal
   * stacks above the auto-opening cart drawer in the browser top layer.
   * The body scroll lock is intentionally left to the cart drawer.
   *
   * @param {'frame' | 'set'} panel
   */
  #open(panel) {
    for (const element of this.querySelectorAll('[data-panel]')) {
      if (element instanceof HTMLElement) {
        element.hidden = element.dataset.panel !== panel;
      }
    }

    this.#currentPanel = panel;

    setTimeout(() => {
      if (!this.refs.dialog.open && this.isConnected) {
        this.refs.dialog.showModal();
      }
    }, OPEN_DELAY);
  }

  /**
   * Dismisses the modal and remembers the choice for this product and panel
   * for the rest of the session, so repeat adds don't nag the customer.
   */
  dismiss = () => {
    if (this.#checkoutIntent) {
      // Closing via X / backdrop / Esc after a checkout intercept: just close
      // and re-arm — the customer may want to edit their basket first.
      this.#checkoutIntent = false;
      this.close();
      return;
    }
    if (this.#currentPanel) this.#markDismissed(this.#currentPanel);
    this.close();
  };

  /**
   * Closes the dialog with the theme's closing animation.
   */
  close = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    try {
      dialog.classList.add('dialog-closing');
      await onAnimationEnd(dialog, undefined, { subtree: false });
    } catch (_) {
      // Animation helper differences between theme versions — close anyway.
    } finally {
      dialog.classList.remove('dialog-closing');
      dialog.close();
    }
  };

  /**
   * @param {'frame' | 'set'} panel
   * @returns {boolean}
   */
  #isDismissed(panel) {
    try {
      return sessionStorage.getItem(this.#dismissKey(panel)) === 'true';
    } catch (_) {
      return false;
    }
  }

  /**
   * @param {'frame' | 'set'} panel
   */
  #markDismissed(panel) {
    try {
      sessionStorage.setItem(this.#dismissKey(panel), 'true');
    } catch (_) {
      // Storage unavailable — modal will simply show again.
    }
  }

  /**
   * @param {'frame' | 'set'} panel
   * @returns {string}
   */
  #dismissKey(panel) {
    return `add-to-cart-upsell:${panel}:${this.#config.productId}`;
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.#busy = busy;
    this.refs.dialog.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /**
   * @param {HTMLElement | undefined} element
   * @param {string | null} message
   */
  #showError(element, message) {
    if (!element) return;
    element.hidden = !message;
    element.textContent = message ?? '';
  }

  /**
   * Formats a minor-unit amount using the shop currency. Returns null when
   * formatting isn't possible so callers can fall back to server-rendered
   * prices.
   *
   * @param {number} cents
   * @returns {string | null}
   */
  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.#config.locale || undefined, {
        style: 'currency',
        currency: this.#config.currency,
      }).format(cents / 100);
    } catch (_) {
      return null;
    }
  }
}

if (!customElements.get('add-to-cart-upsell')) {
  customElements.define('add-to-cart-upsell', AddToCartUpsell);
}